import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import usePublisher from 'api/usePublisher';

import { Flex } from 'components/ui/flex';
import { Separator } from 'components/ui/separator';

import { PREVIEW_CONSTS, previewExampleItems } from './preview.consts';

type ReceiptPreviewProps = {
  receiptLogo: string;
  receiptHeader: string;
  companyAddressStructured?: {
    line1: string;
    line2: string;
    line3: string;
  };
  addFooter?: boolean;
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;
  supportUrl?: string;
};

const PreviewRow = ({
  label,
  value
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Flex direction="row" justify="between" className="w-full">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Flex>
  );
};

export const ReceiptPreview = ({
  receiptLogo,
  receiptHeader,
  addFooter,
  companyAddressStructured,
  termsOfServiceUrl,
  privacyPolicyUrl,
  supportUrl
}: ReceiptPreviewProps) => {
  const [mode, setMode] = useState<'mobile' | 'desktop'>('mobile');
  const { publisherId } = useParams();
  const { getPublisher } = usePublisher(publisherId || undefined);

  const invoiceMerchant = getPublisher.data?.invoiceMerchant || '';

  const cardWidth = useMemo(() => (mode === 'mobile' ? 360 : 480), [mode]);

  return (
    <Flex align="end" className="w-full">
      <Flex direction="row" className="gap-2 mb-2">
        <IconButton
          aria-label="mobile"
          onClick={() => setMode('mobile')}
          color={mode === 'mobile' ? 'primary' : 'default'}
          size="small"
        >
          <SmartphoneOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="desktop"
          onClick={() => setMode('desktop')}
          color={mode === 'desktop' ? 'primary' : 'default'}
          size="small"
        >
          <ViewQuiltOutlinedIcon fontSize="small" />
        </IconButton>
      </Flex>
      <Box
        sx={{
          width: cardWidth,
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}
      >
        <Box
          sx={{
            backgroundColor: receiptHeader ? 'transparent' : '#2f2f35',
            height: 160,
            width: '100%',
            overflow: 'hidden'
          }}
        >
          {receiptHeader ? (
            <Box
              component="img"
              src={receiptHeader}
              alt="header"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
        </Box>
        <Box p={2}>
          <Typography fontWeight={700} mb={1.5} fontSize={16}>
            Purchase Confirmation
          </Typography>
          <Flex direction="row" align="center" className="gap-3 mb-3">
            <Box
              component="img"
              src={receiptLogo}
              alt="logo"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '4px',
                objectFit: 'contain'
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {invoiceMerchant}
            </Typography>
          </Flex>
          <Flex className="gap-2">
            <PreviewRow label="Receipt Number" value="602-758-041" />
            <PreviewRow label="Payment Method" value="Visa (1111)" />
            <PreviewRow
              label="Payment Date"
              value="November 13, 2025 3:21 PM UTC"
            />
            <PreviewRow label="Country" value="Israel" />
            <PreviewRow label="Order ID" value="6915f7714cd9f675fd17df84" />
          </Flex>
          <Separator className="my-6 bg-base-border" />
          <Typography variant="body2" fontWeight={700} gutterBottom>
            Items
          </Typography>
          <Flex className="gap-2.5">
            {previewExampleItems.map((item) => (
              <Flex
                key={item.label}
                direction="row"
                align="center"
                justify="between"
                className="w-full"
              >
                <Flex direction="row" align="center" className="gap-2.5">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: item.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16
                    }}
                  >
                    {item.emoji}
                  </Box>
                  <Typography variant="body2">{item.label}</Typography>
                </Flex>
                <Typography variant="body2">{item.qty}</Typography>
              </Flex>
            ))}
          </Flex>
          <Separator className="my-6 bg-base-border" />
          <Flex
            direction="row"
            justify="between"
            align="center"
          >
            <Typography variant="body2" fontWeight={700}>
              Subtotal
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.SUBTOTAL}
            </Typography>
          </Flex>
          <Flex
            direction="row"
            justify="between"
            align="center"
            className="mt-1.5"
          >
            <Typography variant="body2" fontWeight={700}>
              Tax (18%)
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.TAX}
            </Typography>
          </Flex>
          <Separator className="my-6 bg-base-border" />
          <Flex
            direction="row"
            justify="between"
            align="center"
          >
            <Typography variant="body2" fontWeight={700}>
              Total
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.TOTAL}
            </Typography>
          </Flex>
          <Flex align="center" className="mt-4 gap-1">
            <Typography variant="caption" color="text.secondary">
              Powered by Appcharge
            </Typography>
            <Typography variant="caption" color="text.secondary">
              For questions or assistance, contact{' '}
              {supportUrl ? (
                <a
                  href={supportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 no-underline"
                >
                  Appcharge support
                </a>
              ) : (
                'Appcharge support'
              )}
            </Typography>
          </Flex>
          {addFooter ? (
            <Flex
              direction="row"
              justify="between"
              align="start"
              className="mt-4 gap-4"
            >
              <Flex className="gap-0.5">
                {companyAddressStructured && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {companyAddressStructured.line1}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {companyAddressStructured.line2}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {companyAddressStructured.line3}
                    </Typography>
                  </>
                )}
              </Flex>
              <Flex align="end" className="gap-2">
                <a
                  href={termsOfServiceUrl || 'https://www.appcharge.com/tc'}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 no-underline text-xs"
                >
                  Terms of Service
                </a>
                <a
                  href={privacyPolicyUrl || 'https://www.appcharge.com/privacy'}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 no-underline text-xs"
                >
                  Privacy Policy
                </a>
              </Flex>
            </Flex>
          ) : null}
        </Box>
      </Box>
    </Flex>
  );
};

export default ReceiptPreview;
